import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  Typography
} from '@mui/material';

interface IndicatorItem {
  id: number;
  indicator_name: string;
  level1_name?: string;
  level2_name?: string;
}

interface BulkOpinionSelectDialogProps {
  open: boolean;
  onClose: () => void;
  indicators: IndicatorItem[];
  onConfirm: (selectedIds: number[]) => void;
  loading?: boolean;
}

export default function BulkOpinionSelectDialog({ open, onClose, indicators = [], onConfirm, loading = false }: BulkOpinionSelectDialogProps) {
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      setSelectedIds(indicators.map(x => x.id));
      setKeyword('');
    }
  }, [open, indicators]);

  const filtered = useMemo(() => {
    if (!keyword.trim()) return indicators;
    const k = keyword.trim().toLowerCase();
    return indicators.filter(x =>
      String(x.indicator_name || '').toLowerCase().includes(k) ||
      String(x.level1_name || '').toLowerCase().includes(k) ||
      String(x.level2_name || '').toLowerCase().includes(k)
    );
  }, [indicators, keyword]);

  const isAllChecked = filtered.length > 0 && filtered.every(x => selectedIds.includes(x.id));
  const isIndeterminate = filtered.some(x => selectedIds.includes(x.id)) && !isAllChecked;

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const ids = new Set(selectedIds);
      filtered.forEach(x => ids.add(x.id));
      setSelectedIds(Array.from(ids));
    } else {
      const ids = new Set(selectedIds);
      filtered.forEach(x => ids.delete(x.id));
      setSelectedIds(Array.from(ids));
    }
  };

  const toggleOne = (id: number, checked: boolean) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  };

  const handleConfirm = () => {
    onConfirm?.(selectedIds);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>选择需要生成评价意见的指标</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={2} alignItems="center" mb={2} justifyContent="space-between">
          <FormControlLabel
            sx={{ mr: 1, flexShrink: 0 }}
            control={
              <Checkbox
                checked={isAllChecked}
                indeterminate={isIndeterminate}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            }
            label="全选"
          />
          <TextField
            size="small"
            sx={{ width: 360, maxWidth: '60%' }}
            placeholder="搜索（按三级指标/一级/二级关键词）"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 420, overflowY: 'auto', mt: 1 }}>
          {filtered.map(ind => (
            <Box key={ind.id} display="flex" alignItems="center" py={0.5}>
              <Checkbox
                checked={selectedIds.includes(ind.id)}
                onChange={(e) => toggleOne(ind.id, e.target.checked)}
                size="small"
              />
              <Typography variant="body2" sx={{ mr: 1, minWidth: 64, color: 'text.secondary' }}>{ind.level1_name}</Typography>
              <Typography variant="body2" sx={{ mr: 2, minWidth: 120, color: 'text.secondary' }}>{ind.level2_name}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{ind.indicator_name}</Typography>
            </Box>
          ))}
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>无匹配的指标</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>取消</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || selectedIds.length === 0}>
          {loading ? '生成中...' : `生成所选（${selectedIds.length}）`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


